import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { Pagination } from '../../components/Pagination';
import type { SymptomListItem, FaqCategory } from '../../types';

const LIMIT = 10;

export const SymptomListPage: React.FC = () => {
  const navigate = useNavigate();
  const [symptoms, setSymptoms] = useState<SymptomListItem[]>([]);
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState('');
  const [inputKeyword, setInputKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load categories on mount
  useEffect(() => {
    apiService
      .getCategories()
      .then((cats) => {
        const parents = cats.filter((c) => !c.productCategoryId);
        setCategories(parents);
      })
      .catch(() => setError('カテゴリの取得に失敗しました'));
  }, []);

  const fetchSymptoms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.getSymptoms({
        categoryId,
        keyword: keyword || undefined,
        page,
        limit: LIMIT,
      });
      setSymptoms(result.items);
      setTotal(result.total);
    } catch {
      setError('症状の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [categoryId, keyword, page]);

  useEffect(() => {
    fetchSymptoms();
  }, [fetchSymptoms]);

  const handleCategoryChange = (newId: string) => {
    setCategoryId(newId);
    setPage(1);
    setKeyword('');
    setInputKeyword('');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setKeyword(inputKeyword);
    setPage(1);
  };

  const handleDelete = async (symptom: SymptomListItem) => {
    if (
      !window.confirm(
        `「${symptom.symptomName}」と紐づく回答を削除しますか？この操作は取り消せません。`,
      )
    ) {
      return;
    }
    try {
      await apiService.deleteSymptom(symptom.categoryId, symptom.symptomName);
      fetchSymptoms();
    } catch {
      alert('削除に失敗しました');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">症状検索一覧</h1>
        <button
          onClick={() => navigate('/symptoms/new')}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          + 新規症状を追加
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <div className="w-52">
          <label className="mb-1 block text-xs font-medium text-slate-500">製品カテゴリ</label>
          <select
            value={categoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">すべての製品カテゴリ</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-500">キーワード検索</label>
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={inputKeyword}
              onChange={(e) => setInputKeyword(e.target.value)}
              placeholder="症状名で検索..."
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="submit"
              className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-600 hover:bg-slate-200"
            >
              検索
            </button>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-200 bg-slate-50">
              <th
                className="px-5 py-3 text-left font-semibold text-slate-500"
                style={{ width: 50 }}
              >
                No.
              </th>
              <th className="px-3 py-3 text-left font-semibold text-slate-500">症状名</th>
              <th
                className="px-5 py-3 text-right font-semibold text-slate-500"
                style={{ width: 120 }}
              >
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-slate-400">
                  読み込み中...
                </td>
              </tr>
            ) : symptoms.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-slate-400">
                  症状がありません
                </td>
              </tr>
            ) : (
              symptoms.map((symptom, idx) => (
                <tr
                  key={symptom.symptomName}
                  className={`border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/50' : ''}`}
                >
                  <td className="px-5 py-3 text-slate-400">{(page - 1) * LIMIT + idx + 1}</td>
                  <td className="px-3 py-3">{symptom.symptomName}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() =>
                        navigate(
                          `/symptoms/${encodeURIComponent(symptom.categoryId)}/${encodeURIComponent(symptom.symptomName).replace(/%2F/gi, '%252F')}/edit`,
                        )
                      }
                      className="mr-3 text-blue-600 hover:text-blue-800"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(symptom)}
                      className="text-red-500 hover:text-red-700"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-slate-200 px-5 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">
                全{total}件中 {(page - 1) * LIMIT + 1}-{Math.min(page * LIMIT, total)}件を表示
              </span>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
